import React from "react";
//#region redux
import { displayDialog } from "store/actions/utilActions";
import { ThunkDispatch } from "redux-thunk";
import { useDispatch } from "react-redux";
//#endregion
//#region types
import { FormFieldProps, TableListFieldProps } from "models/propTypes/genericFormProps";
import { DialogEnums } from "models/enums";
import { TranslationKeys } from "translations";
//#endregion
//#region components
import { FieldArray, FieldArrayRenderProps, useFormikContext } from "formik";
import useTableListFormFieldHelpers from "../baseFields/useTableListFormFieldHelpers";
import TableList from "../baseFields/tableList/tableList";
import ReadOnlyField from "../baseFields/readOnlyField";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";

function TableListFormField<T extends Record<string, any>>(props: FormFieldProps<T>) {
  const { name, field, value, readonly } = props;
  const dispatch: ThunkDispatch<any, any, any> = useDispatch();

  const formikProps = useFormikContext();
  const { values } = formikProps;
  const extraProps = field.extraProps as TableListFieldProps<T>;
  const addItemForm = extraProps?.addItemForm
    ? typeof extraProps.addItemForm === "function"
      ? extraProps.addItemForm(values)
      : extraProps.addItemForm
    : undefined;

  const entityText = LocalizationHelperInstance.getFormattedMessage(extraProps.entityText).toLowerCase();

  const { getProperties, getFormMoreActions } = useTableListFormFieldHelpers<T>(
    value,
    extraProps.resultTableProperties,
    extraProps.idFieldKey,
    extraProps.moreActions,
    extraProps.defaultRefField,
    readonly,
    extraProps.hideMoreActionsInForm,
    extraProps.onRemoveItem
  );

  const onAddNewClick = (arrayHelpers: FieldArrayRenderProps) => {
    extraProps.addItemFunction
      ? extraProps.addItemFunction(values)
      : dispatch(
          displayDialog({
            isOpen: false,
            title: `${LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.NewEntity)} ${entityText}`,
            titleIcon: extraProps.icon,
            subTitle: extraProps?.addItemDialogSubTitle,
            dialogType: DialogEnums.DialogType.Content,
            actions: [
              {
                text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Add),
              },
            ],
            children: addItemForm ?? <></>,
            onSubmit: (values: any) => {
              if (values) {
                handleDefaults(values, arrayHelpers);
                arrayHelpers.push(values);
                formikProps.setFieldTouched(name, true, false);
              }
            },
            maxWidth: extraProps?.maxWidth ?? "sm",
            hideDivider: extraProps.hideHeaderDividerInForm,
            fullWidth: extraProps?.fullWidth,
            confirmCloseOnX: extraProps?.confirmCloseOnX,
          })
        );
  };

  const onItemClick = (arrayHelpers: FieldArrayRenderProps, item: any, index: number) => {
    extraProps.addItemFunction
      ? extraProps.addItemFunction(item)
      : dispatch(
          displayDialog({
            isOpen: false,
            title: `${LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Edit)} ${
              item.name ?? entityText
            }`,
            titleIcon: extraProps.icon,
            subTitle: extraProps?.addItemDialogSubTitle,
            dialogType: DialogEnums.DialogType.Content,
            actions: [
              {
                text:
                  extraProps.editItemButtonText ??
                  LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Save),
                disabled: extraProps?.editDialogDisableSave ? extraProps.editDialogDisableSave(item) : false,
              },
            ],
            children: addItemForm ? <addItemForm.type {...addItemForm.props} {...item} /> : <></>,
            onSubmit: (formValues: any) => {
              const id = extraProps.idFieldKey ? item[extraProps.idFieldKey] : item.id;
              // eslint-disable-next-line eqeqeq --- disabling to avoid regression testing if changed
              const itemIndex = id && (value as any[]).findIndex((v: any) => v[extraProps.idFieldKey ?? "id"] == id);
              handleDefaults(formValues, arrayHelpers);
              arrayHelpers.replace(itemIndex ?? index, formValues);
              formikProps.setFieldTouched(name, true, false);
            },
            moreActions: getFormMoreActions(index, item, arrayHelpers),
            maxWidth: extraProps?.maxWidth ?? "sm",
            hideDivider: extraProps.hideHeaderDividerInForm,
            fullWidth: extraProps?.fullWidth,
            confirmCloseOnX: extraProps?.confirmCloseOnX,
          })
        );
  };

  const handleDefaults = (values: any, arrayHelpers: FieldArrayRenderProps) => {
    if (value && value.length > 0 && extraProps.defaultRefField && values[extraProps.defaultRefField]) {
      const currentDefaultIndex = (value as any[]).findIndex(
        (x) => extraProps.defaultRefField && x[extraProps.defaultRefField]
      );
      if (currentDefaultIndex > -1) {
        const currentDefaultItem = value[currentDefaultIndex];
        currentDefaultItem[extraProps.defaultRefField] = false;
        arrayHelpers.replace(currentDefaultIndex, currentDefaultItem);
      }
    }
  };

  const component = (
    <FieldArray
      name={name}
      render={(arrayHelpers) => (
        <TableList
          entityText={entityText}
          properties={getProperties(arrayHelpers)}
          value={value}
          onItemClick={onItemClick.bind(null, arrayHelpers)}
          onAddNewClick={onAddNewClick.bind(null, arrayHelpers)}
          isReadOnly={readonly}
          orderByProperty={extraProps.orderByProperty}
          disableItemClick={extraProps.disableItemClick}
          disableSort={extraProps.disableSort}
          activeKey={extraProps.activeKey}
          searchKeys={extraProps.searchKeys}
          searchPlaceholder={extraProps.searchPlaceholder}
          inactiveGroupTitle={extraProps.inactiveGroupTitle}
          hideHeader={extraProps.hideHeader}
          hideAddNewButton={extraProps.hideAddNewButton}
          pagination={extraProps.pagination}
          maxRowCount={extraProps.maxRowCount}
          stayOnCurrentPage={extraProps.stayOnCurrentPage}
          rememberPositionKey={extraProps.rememberPositionKey}
          restorePosition={extraProps.restorePosition}
          moreButtons={extraProps.moreButtons}
        />
      )}
    />
  );

  return readonly ? (
    <ReadOnlyField label={field.label} href={field.href}>
      {component}
    </ReadOnlyField>
  ) : (
    component
  );
}

export default TableListFormField;
